import React, { useEffect, useRef, useState } from "react";
import styles from "./chart.module.scss";
import { Candle, CandleTypePart, DirectionsOfCandle, TimeFrames } from "../../../shared/types";
import { translate } from "../translation";
import { useAppSelector } from "../../../store/store";
import dayjs from "dayjs";
import {
  colorOfLegendCanvas,
  colorOfLineSimulatedTransactionCanvas,
  colorOfVolumeOnDojiCandle,
  colorOfVolumeOnDownCandle,
  colorOfVolumeOnUpCandle,
  countCandleOnSimulatedTransactionCanvas,
  formulaOfSimulatedTransactionCandleCanvas,
  heightSimulatedTransactionVolumeCanvas,
  lineWidthOfVolumeSimulatedTransactionCanvas,
  offsetChartFromLeftBorderSimulatedTransactionCanvas,
} from "../../../shared/configCanvas";
import { delay, frameGate, getLimitPrices } from "../../../shared/lib/lib";
import {
  buildCandle,
  buildSimulatedTransactionMarks,
  buildVolumeCandle,
  calcOpacityAnScaleForMark,
  scalePrice,
} from "../../../shared/lib/canvas";
import { getRandom } from "../../simulatedTransaction/functions";

interface ICanvasSize {
  width: number;
  height: number;
}

interface Props {
  greetingEnded: boolean;
  chart: Candle[];
  timeframe: TimeFrames;
  typeOrder: string;
  dateTimeOpenOrder: string;
  priceOpenOrder: string;
  language: string;
  ticker: string;
  companyName: string;
  transformCanvas: { widthRatio: number; heightRatio: number };
  movieRefTitle: React.RefObject<HTMLDivElement>;
  setHookSecondTitleIsActive: React.Dispatch<React.SetStateAction<Boolean>>;
}

const Chart = ({
  greetingEnded,
  chart,
  timeframe,
  typeOrder,
  dateTimeOpenOrder,
  priceOpenOrder,
  language,
  ticker,
  companyName,
  transformCanvas,
  movieRefTitle,
  setHookSecondTitleIsActive,
}: Props) => {
  const screenWidth = useAppSelector(state => state.appReduser.screenWidth);

  const chartCanvas = useRef<HTMLCanvasElement>(null);
  const [canvasContext, setCanvasContext] = useState<CanvasRenderingContext2D | null>(null);
  const [canvasSize, setCanvasSize] = useState<ICanvasSize>();
  const [currentChart, setCurrentChart] = useState<Candle[]>(); //график, из chartFlow, который нужно построить сейчас, график в момент времени когда была сделка + несколько свечей
  const [endRecording, setEndRecording] = useState<boolean>(false); //флаг завершения анимации и записи

  const [numberOfOpenCandle, setNumberOfOpenCandle] = useState<number>(); //номер свечи на графике на которой было открытие позиции

  const markRef = useRef<HTMLDivElement>(null);

  //расчет размера canvas, с учетом рандомизации для антиПовтор в соц сети
  useEffect(() => {
    if (screenWidth && transformCanvas) {
      const width = Math.round(973 * transformCanvas.widthRatio);
      const height = Math.round(1007 * transformCanvas.heightRatio);
      setCanvasSize({
        width: width,
        height: height,
      });
    }
  }, [screenWidth, transformCanvas]);

  //изначальное получение canvas графика и настройка
  useEffect(() => {
    if (chartCanvas.current) {
      const canvas = chartCanvas.current;
      const context = canvas.getContext("2d");
      if (context) {
        //это решение вопроса размытой графики
        context.translate(0.5, 0.5);
        setCanvasContext(context);
      }
    }
  }, [chartCanvas]);

  //рисование графика
  useEffect(() => {
    if (canvasSize && canvasContext && currentChart) {
      //очистка холста при перерендере
      canvasContext.clearRect(0, 0, canvasSize.width, canvasSize.height);
      canvasContext.beginPath();
      //константы для графики и различные переменные для смещения и трансформирования размеров графики под размер холста
      const indOfLastCandle = currentChart.length - 1;
      const countCandleOnCanvas = countCandleOnSimulatedTransactionCanvas;
      const colorOfLine = colorOfLineSimulatedTransactionCanvas;
      const scaleXCandle = formulaOfSimulatedTransactionCandleCanvas;
      const { highPrice, lowPrice, highVolume } = getLimitPrices(
        currentChart,
        indOfLastCandle - countCandleOnCanvas + 1,
        indOfLastCandle
      );
      const scaleYChart = scalePrice(
        highPrice,
        lowPrice,
        canvasSize.height - heightSimulatedTransactionVolumeCanvas - 5,
        0
      );
      const scaleYVolume = scalePrice(highVolume, 0, heightSimulatedTransactionVolumeCanvas - 5, 0);
      const startOfScaleY = lowPrice;
      const offsetLeftBorder = offsetChartFromLeftBorderSimulatedTransactionCanvas;
      const stepCandleOnScaleX = formulaOfSimulatedTransactionCandleCanvas.total;
      const colorVolumeOnUpCandle = colorOfVolumeOnUpCandle;
      const colorVolumeOnDownCandle = colorOfVolumeOnDownCandle;
      const colorVolumeOnDojiCandle = colorOfVolumeOnDojiCandle;
      const lineWidthOfVolume = lineWidthOfVolumeSimulatedTransactionCanvas;
      const colorOfLegend = colorOfLegendCanvas;

      //-------рисование графики----------

      //график
      for (let i = indOfLastCandle - countCandleOnCanvas + 1; i <= indOfLastCandle; i++) {
        const newInd = indOfLastCandle - i;
        buildCandle(
          canvasContext,
          canvasSize.width,
          canvasSize.height,
          scaleXCandle,
          scaleYChart,
          startOfScaleY,
          offsetLeftBorder,
          0,
          heightSimulatedTransactionVolumeCanvas,
          currentChart[i],
          newInd,
          colorOfLine,
          true
        );
      }

      //индикатор Volume
      buildVolumeCandle(
        canvasContext,
        currentChart,
        scaleYVolume,
        canvasSize.height,
        canvasSize.width,
        0,
        0,
        stepCandleOnScaleX,
        offsetLeftBorder,
        colorVolumeOnUpCandle,
        colorVolumeOnDownCandle,
        colorVolumeOnDojiCandle,
        colorOfLegend,
        lineWidthOfVolume
      );

      //метка сделки открытия
      if (
        numberOfOpenCandle &&
        markRef.current &&
        currentChart.length - 1 >= numberOfOpenCandle &&
        currentChart[numberOfOpenCandle][CandleTypePart.low] < parseFloat(priceOpenOrder) &&
        currentChart[numberOfOpenCandle][CandleTypePart.high] > parseFloat(priceOpenOrder)
      ) {
        const newOpenInd = indOfLastCandle - numberOfOpenCandle;
        let direction = DirectionsOfCandle.DOJI;
        if (typeOrder === "openingPosition" || typeOrder === "increasePosition") {
          direction = DirectionsOfCandle.UP;
        } else if (typeOrder === "closingPosition") {
          direction = DirectionsOfCandle.DOWN;
        }

        buildSimulatedTransactionMarks(
          canvasContext,
          canvasSize.width,
          canvasSize.height,
          scaleXCandle,
          scaleYChart,
          startOfScaleY,
          offsetLeftBorder,
          0,
          heightSimulatedTransactionVolumeCanvas,
          priceOpenOrder,
          newOpenInd,
          colorOfLine,
          direction
        );
      }
    }
  }, [canvasContext, canvasSize, currentChart, timeframe]);

  //определение свечей на которых было открытие, добавления и закрытие позиции
  useEffect(() => {
    const dtOpen = dateTimeOpenOrder ? dayjs(dateTimeOpenOrder).tz("America/New_York") : undefined;
    let indOfCandleWhereOpenPosition = undefined;

    chart.forEach((candle, index) => {
      const dtOpenCandle = dayjs.tz(candle[CandleTypePart.begin], "America/New_York");
      if (dtOpen && dtOpenCandle <= dtOpen) {
        indOfCandleWhereOpenPosition = index;
      }
    });

    setNumberOfOpenCandle(indOfCandleWhereOpenPosition);
  }, [chart, dateTimeOpenOrder, priceOpenOrder]);

  //создаю график в нужный для отображения момент времени (момент сделки + несколько свечей)
  useEffect(() => {
    if (chart.length !== 0 && numberOfOpenCandle) {
      const currentChart = JSON.parse(JSON.stringify(chart)).slice(0, numberOfOpenCandle + 2);
      setCurrentChart(currentChart);
    }
  }, [chart, numberOfOpenCandle]);

  //цикл анимации метки сделки
  const markOrderAnimation = async () => {
    await delay(200);

    //пауза 700ms, которая снимается на видео между вступлением и пульсов метки сделки
    for (let i = 0; i < 21; i++) {
      //сообщаем в puppeteer, что кадр отрисован и ждем обратный ответ что скриншот снят
      await frameGate();
      await delay(200);
    }

    //цикл самой анимации
    //снимаем видео с FPS 30, т.е. 45 кадров это будет 1.5 сек на полный цикл появления метки
    for (let i = 0; i < 45; i++) {
      if (markRef.current) {
        //меняем состояние метки
        const { opacity, scale } = calcOpacityAnScaleForMark(i);
        markRef.current.style.opacity = opacity.toString();
        markRef.current.style.transform = `scale(${scale})`;
        //сообщаем в puppeteer, что кадр отрисован и ждем обратный ответ что скриншот снят
        await frameGate();
        await delay(200);
      }
    }

    //в какой-то момент скрываем первый заголовок и проявляем второй
    if (movieRefTitle.current) {
      const startHidingTimingFirstTitle = 0;
      const endHidingTimingFirstTitle = 300;
      const startShowingTimingSecondTitle = 500;
      const endShowingTimingSecondTitle = 1000;

      const interval = 33; //fps 30
      let opacityTitle = 1;

      const durationHidingFirstTitle = endHidingTimingFirstTitle - startHidingTimingFirstTitle;
      const durationShowingSecondTitle = endShowingTimingSecondTitle - startShowingTimingSecondTitle;
      const countIterationHidingFirstTitle = Math.floor(durationHidingFirstTitle / interval);
      const countIterationShowingSecondTitle = Math.floor(durationShowingSecondTitle / interval);
      const opacityIncrementHidingFirstTitle = 1.0 / countIterationHidingFirstTitle;
      const opacityIncrementShowingSecondTitle = 1.0 / countIterationShowingSecondTitle;

      for (let i = 0; i < endShowingTimingSecondTitle / interval; i++) {
        //сообщаем в puppeteer, что кадр отрисован и ждем обратный ответ что скриншот снят
        await frameGate();
        await delay(200);
        const timing = i * interval;

        if (timing < endHidingTimingFirstTitle) {
          //скрываем заголовок (чтобы скрыть первый заголовок, он сейчас активен)
          if (timing + interval < endHidingTimingFirstTitle) {
            movieRefTitle.current.style.opacity = opacityTitle.toString();
            opacityTitle -= opacityIncrementHidingFirstTitle;
          } else {
            movieRefTitle.current.style.opacity = "0.0";
            opacityTitle = 0;
          }
        } else if (timing < startShowingTimingSecondTitle) {
          //делаем подмену заголовков (пока он скрыт)
          setHookSecondTitleIsActive(true);
        } else if (timing < endShowingTimingSecondTitle) {
          //проявляем заголовок (чтобы показать второй заголовок, он сейчас активен)
          if (timing + interval < endShowingTimingSecondTitle) {
            movieRefTitle.current.style.opacity = opacityTitle.toString();
            opacityTitle += opacityIncrementShowingSecondTitle;
          } else {
            movieRefTitle.current.style.opacity = "1.0";
            opacityTitle = 1;
          }
        }
      }
    }

    //пауза 1500ms, которая снимается на видео, между пульсом метки сделки и завершением
    for (let i = 0; i < 45; i++) {
      //сообщаем в puppeteer, что кадр отрисован и ждем обратный ответ что скриншот снят
      await frameGate();
      await delay(200);
    }
  };

  //если анимация приветствия закончена, то делаем анимацию на графике
  useEffect(() => {
    if (greetingEnded) {
      const func = async () => {
        await markOrderAnimation();
        await delay(200);
        setEndRecording(true);
      };
      func();
    }
  }, [greetingEnded]);

  //возврат всех состояний в исходное - подготовка к следующему видео
  useEffect(() => {
    if (endRecording) {
      // сообщаем в puppeteer об окончании цикла отрисовки кадров для видео:
      window.__captureStopFlag = true;
      if (typeof window.__puppeteerAllDone === "function") {
        window.__puppeteerAllDone();
      }
      setEndRecording(false);
    }
  }, [endRecording]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <p className={styles.title}>{translate(language, "timeframe-" + timeframe)}</p>
        <div className={styles.name}>
          <p className={styles.shortName}>{ticker}</p>
          <p className={styles.fullName}>{companyName}</p>
        </div>
        <div className={styles.markWrap}>
          <div className={styles.mark} ref={markRef}>
            {typeOrder === "openingPosition" || typeOrder === "increasePosition" ? (
              <p className={styles.open}>
                <svg viewBox="0 0 24 24" width="45" height="45" aria-label="Buy" role="img">
                  <title>Buy</title>
                  <path d="M12 19V5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                  <path
                    d="M5.5 11.5L12 5l6.5 6.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </p>
            ) : typeOrder === "closingPosition" ? (
              <p className={styles.close}>
                <svg viewBox="0 0 24 24" width="45" height="45" aria-label="Sell" role="img">
                  <title>Sell</title>
                  <path d="M12 5v14" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                  <path
                    d="M5.5 12.5L12 19l6.5-6.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </p>
            ) : null}
          </div>
        </div>
        <div className={styles.canvasWrap}>
          <canvas ref={chartCanvas} width={canvasSize?.width} height={canvasSize?.height}>
            Ваш браузер не поддерживается!
          </canvas>
        </div>
      </div>
    </div>
  );
};

export default Chart;
